/**
 * Structured Data interfaces
 *
 * Structured data (sometimes referred to as JSON schema) are pieces of
 * metadata that help improve a page's SEO ranking. Pages with structured data
 * can be featured in special search snippets. For example, a PHP Jobs page
 * with FAQ structured data can be featured when a user searches for
 * "What is PHP?"
 *
 * Google recommends to follow the Schema.org vocabulary represented in
 * JSON-LD markup
 *
 * References
 * https://developers.google.com/search/docs/guides/intro-structured-data
 * https://schema.org/
 * https://json-ld.org/
 */

/**
 * List of questions and answers about a topic. A page can have multiple
 * Q&A pairs
 */
interface FaqMainEntity {
  '@type': string;
  name: string;
  acceptedAnswer: {
    '@type': string;
    text: string;
  };
}

export interface FaqStructuredData {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: readonly FaqMainEntity[];
}

export interface PersonStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Person';
  name: string;
  url: string;
}

export interface OrganizationStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Organization';
  logo: string;
  name: string;
  url?: string;
  sameAs?: string[];
}

export interface ArticleStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Article';
  author: PersonStructuredData | OrganizationStructuredData;
  datePublished: string;
  dateModified?: string;
  headline: string;
  image?: string;
  publisher: OrganizationStructuredData;
}

export interface WebSiteStructuredData {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  name: string;
  url: string;
}

/**
 * Rating for a structured data object e.g. overall rating out of 5 for a Product.
 *
 * See: https://schema.org/AggregateRating
 */
export interface AggregateRatingStructuredData {
  '@context': 'https://schema.org';
  '@type': 'AggregateRating';
  ratingValue: number;
  reviewCount: number;
}

/**
 * Any offered product or service
 *
 * See: https://schema.org/Product
 */
export interface ProductStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Product';
  name: string;
  description: string;
  aggregateRating?: AggregateRatingStructuredData;
}

export enum StructuredDataClassName {
  ARTICLE_SCHEMA = 'artcle-schema',
  FAQ_SCHEMA = 'faq-schema',
  PERSON_SCHEMA = 'person-schema',
  ORGANIZATION_SHCEMA = 'organization-schema',
  WEBSITE_SCHEMA = 'website-schema',
  PRODUCT_SCHEMA = 'product-schema',
}

export type StructuredData =
  | FaqStructuredData
  | ArticleStructuredData
  | PersonStructuredData
  | OrganizationStructuredData
  | WebSiteStructuredData
  | ProductStructuredData;
